import { Fragment, useRef } from "react"
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { utils, writeFile } from "sheetjs-style-v2"
import { alignCellsVertically, autoFitColumnsWidth, boldenHeaderRow } from "@utils/excel"
import DownloadButton from "@components/DownloadButton"
import { DataSetsWithMetadata } from "@features/results/Result/ChartsContainer/BarStringsChart.utils"

type BarStringsChartTableProps = {
  dataSets: DataSetsWithMetadata
  timestepLabels: number[]
  exportFileName: string
}

export const BarStringsChartTable = ({
  dataSets,
  timestepLabels,
  exportFileName,
}: BarStringsChartTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)

  const handleDownloadTable = () => {
    if (!tableRef.current) return

    const sheet = utils.table_to_sheet(tableRef.current, { raw: true })
    boldenHeaderRow(sheet)
    alignCellsVertically(sheet, "center")
    autoFitColumnsWidth(sheet)

    const workBook = utils.book_new()
    utils.book_append_sheet(workBook, sheet)

    writeFile(workBook, exportFileName + ".xlsx")
  }

  return (
    <>
      <Box sx={{ alignSelf: "flex-start", mb: 1 }}>
        <DownloadButton onClick={handleDownloadTable} />
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Node</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Option</TableCell>
              {timestepLabels.map((timeStep) => (
                <TableCell key={timeStep} sx={{ fontWeight: "bold" }}>
                  {timeStep}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(dataSets.entries()).map(([nodeKey, { values }]) => {
              const optionEntries = Array.from(values.entries()).sort(([a], [b]) =>
                a.localeCompare(b),
              )
              return (
                <Fragment key={nodeKey}>
                  {optionEntries.map(([option, counts], optionIndex) => (
                    <TableRow key={`${nodeKey}-${option}`}>
                      {optionIndex === 0 && (
                        <TableCell rowSpan={optionEntries.length}>{nodeKey}</TableCell>
                      )}
                      <TableCell>{option}</TableCell>
                      {timestepLabels.map((timestep, tsIndex) => (
                        <TableCell key={timestep}>{counts[tsIndex] ?? 0}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
